import React, { useCallback } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { getApplicationForProject } from '@atlassian/jira-shared-types/src/application.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { getEdition } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsEmbedMode } from '@atlassian/jira-issue-view-embed-mode/src/index.tsx';
import type { ExperienceAttributes } from '@atlassian/jira-experience-tracker/src/common/types.tsx';
import { isHttpClientErrorResponse } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { isAbortableError } from '@atlassian/jira-fetch/src/utils/is-abortable-error.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useExperienceAbort } from '@atlassian/jira-experience-tracker/src/ui/experience-abort/index.tsx';
import { EXPERIENCE_NAME } from '../../constants.tsx';

const ANALYTICS_SOURCE = 'issue-navigator';

export type NinErrorBoundaryProps = {
	children: React.ReactNode;
	id: string;
	packageName: string;
};

export const NinErrorBoundary = ({ children, id, packageName }: NinErrorBoundaryProps) => {
	const { data: project } = useProjectContext();
	const onExperienceStart = useExperienceStart({
		experience: EXPERIENCE_NAME,
		experienceId: undefined,
		analyticsSource: ANALYTICS_SOURCE,
	});
	const appEditions = useAppEditions();
	const application = project?.projectType ? getApplicationForProject(project.projectType) : null;
	const edition = application ? getEdition(application, appEditions) : null;
	let analyticsAttributes: ExperienceAttributes = {
		application,
		edition,
	};

	const isEmbed = useIsEmbedMode();
	analyticsAttributes = {
		...analyticsAttributes,
		isEmbed,
	};

	const onExperienceFail = useExperienceFail({
		experience: EXPERIENCE_NAME,
		attributes: analyticsAttributes,
	});

	const onExperienceAbort = useExperienceAbort({
		experience: EXPERIENCE_NAME,
		attributes: analyticsAttributes,
	});

	const onError = useCallback(
		(location: string, error: Error) => {
			if (!isHttpClientErrorResponse(error)) {
				onExperienceStart();
				if (isAbortableError(error) && fg('improve_abortable_errors_experience_tracker')) {
					onExperienceAbort(location, { errorMessage: error.message });
					return;
				}
				onExperienceFail(location, error);
			}
		},
		[onExperienceFail, onExperienceStart, onExperienceAbort],
	);
	return (
		<JSErrorBoundary id={id} packageName={packageName} onError={onError} sendToPrivacyUnsafeSplunk>
			{children}
		</JSErrorBoundary>
	);
};
